import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, BookOpen, Users, Download, Smartphone, Star, Layers } from 'lucide-react';
import EditorialCard from '../components/EditorialCard';
import { getHomeCards, getHomeWebtoons, getHomeCollectionsButtons } from '../services/cms';
import { useTexts } from '../context/TextsContext';

const SectionTitle = ({ icon: Icon, children, to, cta, sub }) => (
  <div className="flex items-end justify-between mb-6">
    <div>
      <h2 className="font-display font-700 text-2xl sm:text-3xl text-foreground flex items-center gap-2.5">
        {Icon && <Icon className="w-7 h-7 text-primary" />} {children}
      </h2>
      {sub && <p className="text-muted-foreground font-600 mt-1">{sub}</p>}
    </div>
    {to && <Link to={to} className="hidden sm:flex items-center gap-1 text-primary font-800 hover:gap-2 transition-all shrink-0">{cta || 'Tout voir'} <ArrowRight className="w-4 h-4" /></Link>}
  </div>
);

const Home = () => {
  const { t, bool } = useTexts();
  const [cards, setCards] = useState([]);
  const [favoris, setFavoris] = useState([]);
  const [decouverte, setDecouverte] = useState([]);
  const [collButtons, setCollButtons] = useState([]);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosTip, setShowIosTip] = useState(false);

  useEffect(() => {
    getHomeCards().then((c) => setCards(c || [])).catch(() => setCards([]));
    getHomeWebtoons().then((d) => { setFavoris(d?.favoris || []); setDecouverte(d?.decouverte || []); }).catch(() => {});
    getHomeCollectionsButtons().then((b) => setCollButtons(b || [])).catch(() => setCollButtons([]));
  }, []);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const installApp = async () => {
    if (deferredPrompt) { deferredPrompt.prompt(); await deferredPrompt.userChoice; setDeferredPrompt(null); }
    else { setShowIosTip((v) => !v); }
  };

  const CtaButton = ({ dest, className, children }) => {
    if (dest && !dest.startsWith('/')) return <a href={dest} target="_blank" rel="noreferrer noopener" className={className}>{children}</a>;
    return <Link to={dest || '/catalogue'} className={className}>{children}</Link>;
  };

  const heroTitle = t('home_hero_titre', 'Découvrez le patrimoine en webtoon');
  const hl = t('home_hero_highlight', 'webtoon');
  const heroImage = t('home_hero_image', '');
  const renderTitle = () => {
    if (hl && heroTitle.includes(hl)) {
      const parts = heroTitle.split(hl);
      return parts.map((p, i) => (
        <React.Fragment key={i}>{p}{i < parts.length - 1 && <span style={{ color: '#ffd100' }}>{hl}</span>}</React.Fragment>
      ));
    }
    return heroTitle;
  };

  return (
    <div>
      {/* 2. NOS WEBTOONS FAVORIS (4 grandes cartes) */}
      {favoris.length > 0 && (
      <section className="max-w-7xl mx-auto px-4 pt-12 pb-8" data-testid="home-favoris">
        <SectionTitle icon={Star} to="/catalogue" cta="Tout le catalogue">Nos webtoons favoris</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {favoris.map((e) => (
            <Link key={e.source_id} to={`/fiche/${encodeURIComponent(e.slug)}`} data-testid={`home-fav-${e.slug}`}
              className="card-hover group bg-card rounded-3xl overflow-hidden border border-border shadow-sm">
              <div className="relative aspect-[4/5] overflow-hidden">
                {e.image_url ? <img src={e.image_url} alt={e.titre} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-muted" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-display font-700 text-white text-lg leading-tight line-clamp-2">{e.titre}</h3>
                  {e.sous_titre && <p className="text-white/80 text-xs font-700 mt-1 flex items-center gap-1 truncate"><Users className="w-3 h-3 shrink-0" /> {e.sous_titre}</p>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* 3. À DÉCOUVRIR AUSSI (8 petites cartes) */}
      <section className="max-w-7xl mx-auto px-4 py-8" data-testid="home-decouverte">
        <SectionTitle icon={Sparkles} to="/catalogue" cta="Voir tout">À découvrir aussi</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {decouverte.map((e) => (
            <Link key={e.source_id} to={`/fiche/${encodeURIComponent(e.slug)}`} data-testid={`home-disc-${e.slug}`}
              className="card-hover group bg-card rounded-2xl overflow-hidden border border-border">
              <div className="relative aspect-square overflow-hidden">
                {e.image_url ? <img src={e.image_url} alt={e.titre} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : <div className="w-full h-full bg-muted" />}
              </div>
              <div className="p-3">
                <h3 className="font-display font-600 text-sm leading-snug line-clamp-2 min-h-[36px] group-hover:text-primary transition-colors">{e.titre}</h3>
                {e.sous_titre && <p className="text-[11px] text-muted-foreground font-700 mt-1 flex items-center gap-1 truncate"><Users className="w-3 h-3 shrink-0" /> {e.sous_titre}</p>}
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-6">
          <Link to="/catalogue" data-testid="home-see-all-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-800 hover:scale-105 transition-transform shadow-lg focus-visible:ring-4 focus-visible:ring-primary/40">
            <BookOpen className="w-5 h-5" /> Voir tous les webtoons
          </Link>
        </div>
      </section>

      {/* 4. PROJETS À DÉCOUVRIR (accueil éditorial) */}
      {cards.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-8" data-testid="home-editorial">
          <SectionTitle to="/collections" cta="Voir les collections">{t('home_editorial_titre', 'Projets à découvrir')}</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {cards.map((c, i) => (
              <EditorialCard key={c.id} card={c} testid={`home-card-${i}`} style={{ animationDelay: `${i * 60}ms` }} />
            ))}
          </div>
          <div className="mt-6">
            <Link to="/collections" data-testid="home-discover-collection-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-secondary text-secondary-foreground font-800 hover:scale-105 transition-transform shadow-lg focus-visible:ring-4 focus-visible:ring-secondary/40">
              <ArrowRight className="w-5 h-5" /> Découvrir les collections
            </Link>
          </div>
        </section>
      )}

      {/* 5. COLLECTIONS (boutons colorés) */}
      {collButtons.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-8" data-testid="home-collections-buttons">
          <SectionTitle icon={Layers} to="/collections" cta="Toutes les collections">Les collections Citytoon</SectionTitle>
          <div className="flex flex-wrap gap-3">
            {collButtons.map((c) => (
              <Link key={c.slug} to={`/collection/${encodeURIComponent(c.slug)}`} data-testid={`home-collection-btn-${c.slug}`}
                className="px-5 py-3 rounded-full font-800 text-white shadow-md hover:scale-105 transition-transform focus-visible:ring-4 focus-visible:ring-primary/30" style={{ background: c.color }}>
                {c.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* 6. BANDEAU « Une équipe / Contactez-nous » */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="hero-flames rounded-3xl px-8 py-12 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
          <div className="relative">
            <h2 className="font-display font-700 text-3xl sm:text-4xl mb-3">{t('home_cta_banner_titre', 'Une équipe, des idées, des projets')}</h2>
            <p className="text-white/90 font-600 max-w-xl mx-auto mb-6">{t('home_cta_banner_texte', 'Vous avez un territoire, un patrimoine ou une histoire à raconter ? Contactez Citytoon.')}</p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-primary font-800 hover:scale-105 transition-transform shadow-xl">{t('home_cta_banner_label', 'Contactez-nous')} <ArrowRight className="w-5 h-5" /></Link>
          </div>
        </div>
      </section>

      {/* 7. BANDEAU PATRIMOINE (éditable + image) */}
      <section className="max-w-7xl mx-auto px-4 py-8" data-testid="home-heritage-banner">
        <div className="relative overflow-hidden rounded-3xl">
          {heroImage
            ? <><img src={heroImage} alt="" className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-black/45" /></>
            : <div className="hero-flames absolute inset-0" />}
          <div className="relative px-8 py-14 sm:px-12 sm:py-20 text-white max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur text-sm font-800 mb-5">
              <Sparkles className="w-4 h-4" /> {t('home_hero_surtitre', 'Le webtoon : média d’avenir !')}
            </span>
            <h2 className="font-display font-700 text-4xl sm:text-5xl leading-[1.05] mb-4">{renderTitle()}</h2>
            <p className="text-lg text-white/90 font-600 mb-7">{t('home_hero_paragraphe', 'Histoire, villes et territoires racontés en bandes dessinées verticales, à lire d’un simple scroll.')}</p>
            <div className="flex flex-wrap gap-3">
              <CtaButton dest={t('home_cta1_dest', '/catalogue')} className="flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-primary font-800 hover:scale-105 transition-transform shadow-xl">
                <BookOpen className="w-5 h-5" /> {t('home_cta1_label', 'Explorer le catalogue')}
              </CtaButton>
              <CtaButton dest={t('home_cta2_dest', '/collections')} className="flex items-center gap-2 px-6 py-3.5 rounded-full bg-secondary text-secondary-foreground font-800 hover:scale-105 transition-transform shadow-xl">
                {t('home_cta2_label', 'Les collections')} <ArrowRight className="w-5 h-5" />
              </CtaButton>
            </div>
          </div>
        </div>
      </section>

      {/* 8. INSTALLER L'APPLICATION (PWA) */}
      {bool('home_install_enabled', true) && (
        <section className="max-w-7xl mx-auto px-4 py-8 pb-14" data-testid="home-install">
          <div className="rounded-3xl border border-secondary/40 bg-secondary/10 px-6 py-8 sm:px-10 flex flex-col sm:flex-row items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-secondary text-secondary-foreground flex items-center justify-center shrink-0 shadow-lg"><Smartphone className="w-8 h-8" /></div>
            <div className="flex-1 text-center sm:text-left">
              <h2 className="font-display font-700 text-2xl text-foreground mb-1">{t('home_install_titre', 'Installez l’application Citytoon')}</h2>
              <p className="text-foreground/75 font-600">{t('home_install_texte', 'Ajoutez Citytoon à votre écran d’accueil pour lire vos webtoons en plein écran, comme une vraie application.')}</p>
              {showIosTip && <p className="text-sm text-secondary font-700 mt-2" data-testid="home-install-ios">{t('home_install_ios', 'Sur iPhone/iPad : appuyez sur le bouton Partager de Safari, puis « Sur l’écran d’accueil ».')}</p>}
            </div>
            <button onClick={installApp} data-testid="home-install-btn" className="flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary text-white font-800 hover:scale-105 transition-transform shadow-lg shadow-primary/25 focus-visible:ring-4 focus-visible:ring-primary/40 shrink-0">
              <Download className="w-5 h-5" /> {t('home_install_label', 'Installer l’application')}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
